import * as React from "react";

import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { MUI_BUTTON_BASE_RESET } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";

// A `<button>` that inks under the finger — MUI's `ButtonBase`, minus the
// looks.
//
// **Its own component because the ripple is per-surface.** `useTouchRipple`
// holds the circles for the one element it is spread onto, so a list whose
// rows share a single hook washes every row on every press. The repo already
// grew this shape twice by hand (`multi-select-field.tsx`'s `OptionRow`,
// `tree-select.tsx`'s row button) before it was named; a third copy in
// `lookup-select-field.tsx` is what made it a component.
//
// It carries **no appearance at all** beyond `relative`, which the ripple
// layer needs to resolve against the button rather than the nearest positioned
// ancestor, and `MUI_BUTTON_BASE_RESET`. A menu row, an option row and a card
// area look nothing alike; what they share is the press feedback, and that is
// all this owns. `IconButton` is this plus MUI's icon-button box.
//
// The reset is not decoration — it is four declarations off `ButtonBaseRoot`
// that every donor row, chip and card area inherits and that a bare `<button>`
// does not: `cursor: pointer` (`ButtonBase.js:63`), `user-select: none`
// (`:64`), `-webkit-tap-highlight-color: transparent` (`:52`), and
// `cursor: default` once disabled (`:76-79`). Without them these surfaces hover
// under an arrow where the donor shows a hand, and their labels drag-select
// mid-press. It sits *before* `className` so a caller can still override.
//
// No `overflow-hidden` either: the layer clips itself at `rounded-[inherit]`,
// and `overflow: hidden` on the host would clip its children's **hit area**
// along with their paint — which on a round control kills the corners its
// square glyph still covers.

export interface RippleButtonProps extends React.ComponentProps<"button"> {
  /**
   * MUI's `centerRipple` (`IconButton.js:215`) — the ink ignores where the
   * pointer landed and blooms inscribed from the middle. `IconButton` is the
   * one `ButtonBase` user that turns it on.
   */
  center?: boolean;
  /**
   * MUI's `disableRipple`, for the surfaces the donor turns press feedback off
   * on. Everything else about the button is unchanged, as it is in MUI.
   */
  disableRipple?: boolean;
  /**
   * MUI's `focusRipple` — whether a Space press inks. **Defaults to `false`,
   * as `ButtonBase`'s does** (`ButtonBase.js:105`): a menu row, a chip and a
   * card area are all silent under Space in the source, so `IconButton` sets
   * it here and a bare `RippleButton` standing in for a `MenuItem` does not.
   *
   * **Check the donor before taking the default** — more `ButtonBase` users
   * set it than the two this app happens to port. `RippleOptions.focusRipple`
   * has the roll-call, and is the one place it is written down.
   */
  focusRipple?: boolean;
  /**
   * The centred ripple MUI breathes inside a keyboard-focused `ButtonBase`
   * (`ButtonBase.js:202-203`) — off by default here (`touch-ripple.tsx`, "No
   * focus pulsate by default"), and turned on by a surface that draws no focus
   * ring of its own, for which it is the donor's only focus cue: MUI's
   * `ToggleButton` (`mui-toggle-button-group`), as `MuiTab` is.
   */
  focusPulsate?: boolean;
}

export function RippleButton({
  center = false,
  disableRipple = false,
  focusRipple = false,
  focusPulsate = false,
  type = "button",
  className,
  children,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  onContextMenu,
  onDragLeave,
  onTouchMove,
  ...props
}: RippleButtonProps) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple({
    center,
    disabled: props.disabled,
    focusPulsate,
  });

  return (
    <button
      type={type}
      {...props}
      // All eleven arms composed, never spread over the caller's: the ripple
      // starts on `pointerdown` and is retired by whichever of the others
      // arrives, and `onFocus` is where it latches whether this focus is the
      // keyboard kind. See `composeRippleProps`.
      {...composeRippleProps<HTMLButtonElement>(
        rippleTargetProps,
        {
          onPointerDown,
          onPointerUp,
          onPointerLeave,
          onPointerCancel,
          onKeyDown,
          onKeyUp,
          onFocus,
          onBlur,
          onContextMenu,
          onDragLeave,
          onTouchMove,
        },
        { disableRipple, focusRipple },
      )}
      className={cn("relative", MUI_BUTTON_BASE_RESET, className)}
    >
      {children}
      {disableRipple ? null : rippleLayer}
    </button>
  );
}
